import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/data';

const categoryNames: Record<string, string> = {
  dongzuo: '动作片',
  xiju: '喜剧片',
  aiqing: '爱情片',
  kongbu: '恐怖片',
  kehuan: '科幻片',
  dongman: '动漫',
  juqing: '剧情片',
  jilu: '纪录片',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year'>('rating');

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || categoryNames[slug || ''] || slug || '';

  const filteredMovies = useMemo(() => {
    const list = movies.filter((m) => m.category === slug);
    if (sortBy === 'rating') {
      return [...list].sort((a, b) => b.rating - a.rating);
    }
    return [...list].sort((a, b) => b.year - a.year);
  }, [slug, sortBy]);

  const seoData = {
    title: `${categoryName} - 777影院 | 高清${categoryName}免费在线观看`,
    description: `777影院${categoryName}频道，提供最新最全的${categoryName}高清资源，免费在线观看。每日更新，让你畅享${categoryName}的精彩世界。`,
    keywords: `${categoryName},777影院,7777影院,免费${categoryName},在线观看${categoryName},高清${categoryName}`,
    canonicalUrl: `https://777cinema.com/category/${slug}`,
    ogType: 'website',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://777cinema.com/' },
      { '@type': 'ListItem', position: 2, name: categoryName },
    ],
  };

  return (
    <>
      <SEOHelmet data={seoData} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: categoryName },
            ]} />

            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950">
                  {categoryName}
                </h1>
                {currentCategory && (
                  <p className="text-sm text-foreground-600 mt-1">{currentCategory.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'rating'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 border border-background-200'
                  }`}
                >
                  评分最高
                </button>
                <button
                  onClick={() => setSortBy('year')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'year'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 border border-background-200'
                  }`}
                >
                  最新上映
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex gap-2 flex-wrap mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 border border-background-200 hover:border-primary-300'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} categoryName={categoryName} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-movie-line text-2xl"></i>
              </div>
              <p className="text-foreground-600">该分类暂无电影，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}